export function parseTimestamp(ts: string | number | null | undefined): number | null {
  if (ts === null || ts === undefined) return null;
  if (typeof ts === "number") return ts < 1e12 ? ts * 1000 : ts;
  const parsed = Date.parse(ts);
  return Number.isNaN(parsed) ? null : parsed;
}

export function relativeTime(ts: string | number | null | undefined, now = Date.now()): string {
  const t = parseTimestamp(ts);
  if (t === null) return "never";
  const diffMs = now - t;
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);
  const sec = Math.round(abs / 1000);
  let out: string;
  if (sec < 5) out = "just now";
  else if (sec < 60) out = `${sec}s`;
  else if (sec < 3600) out = `${Math.round(sec / 60)}m`;
  else if (sec < 86400) out = `${Math.round(sec / 3600)}h`;
  else out = `${Math.round(sec / 86400)}d`;
  if (out === "just now") return out;
  return future ? `in ${out}` : `${out} ago`;
}

export function clockTime(ts: string | number | null | undefined): string {
  const t = parseTimestamp(ts);
  if (t === null) return "--:--:--";
  return new Date(t).toLocaleTimeString(undefined, { hour12: false });
}

// Local date-time (2026-09-25 D2 restart-evidence readability task) — a
// raw ISO instant (D2's own before_observed_at/after_observed_at) shown in
// the viewer's own locale/timezone, never a bare UTC string.
export function formatLocalDateTime(ts: string | number | null | undefined): string {
  const t = parseTimestamp(ts);
  if (t === null) return "--";
  return new Date(t).toLocaleString();
}

// Plain calendar date, no time-of-day — 2026-09-26 operator-review fix
// (MeshCore neighbours section: "Counts since <date>", not a relative age
// or a full timestamp, since the point is a fixed start of the counting
// window, not "how long ago").
export function formatDateOnly(ts: string | number | null | undefined): string | null {
  const t = parseTimestamp(ts);
  if (t === null) return null;
  return new Date(t).toLocaleDateString();
}

// Human-readable MAGNITUDE of a duration in seconds — whole units under an
// hour, one decimal from an hour up (2026-09-25 D2 readability task: "2.4
// years back", "13 min later" are both exactly this precision).
function formatDurationMagnitude(absSeconds: number): string {
  const MIN = 60;
  const HOUR = 3600;
  const DAY = 86400;
  const YEAR = 365.25 * DAY;
  if (absSeconds < MIN) return `${Math.round(absSeconds)} sec`;
  if (absSeconds < HOUR) return `${Math.round(absSeconds / MIN)} min`;
  if (absSeconds < DAY) return `${(absSeconds / HOUR).toFixed(1)} hr`;
  if (absSeconds < YEAR) return `${(absSeconds / DAY).toFixed(1)} days`;
  return `${(absSeconds / YEAR).toFixed(1)} years`;
}

// A SIGNED duration between two fixed points (D2's own jump_seconds —
// always negative by construction, a backward clock jump — and
// elapsed_seconds, always positive) — 2026-09-25 D2 readability task:
// "jump/elapsed seconds render as human durations ('2.4 years back', '13
// min later')". Deliberately "back"/"later", never "ago"/"in" (relativeTime
// above) — this is a duration between two evidence timestamps, not
// relative to the current wall clock.
export function formatSignedDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "--";
  if (seconds === 0) return "0 sec";
  const magnitude = formatDurationMagnitude(Math.abs(seconds));
  return seconds < 0 ? `${magnitude} back` : `${magnitude} later`;
}

// Clock offset (2026-09-26, "Clock drift per node" board item) — finer
// precision than formatSignedDuration's own single-unit magnitude (D2's
// "2.4 years back" is fine for a rare boot-loop event; a per-node clock
// reading wants "4 min 12 s ahead", not a rounded "4 min"). Combines the
// two largest non-zero units (min+sec / hr+min / days+hr), one decimal
// only once past days (matching formatSignedDuration's own precision
// there, since sub-day resolution stops being meaningful at that scale).
// "ahead"/"behind" (the chosen wording), never "back"/"later" —
// formatSignedDuration's suffixes describe a jump BETWEEN two evidence
// timestamps, this describes a node's clock relative to trusted receipt
// time right now.
export function formatClockOffset(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "--";
  if (seconds === 0) return "exact";
  const direction = seconds < 0 ? "behind" : "ahead";
  const abs = Math.abs(seconds);
  const MIN = 60;
  const HOUR = 3600;
  const DAY = 86400;
  const YEAR = 365.25 * DAY;
  let magnitude: string;
  if (abs < MIN) {
    magnitude = `${Math.round(abs)} s`;
  } else if (abs < HOUR) {
    const min = Math.floor(abs / MIN);
    const sec = Math.round(abs % MIN);
    magnitude = sec > 0 ? `${min} min ${sec} s` : `${min} min`;
  } else if (abs < DAY) {
    const hr = Math.floor(abs / HOUR);
    const min = Math.round((abs % HOUR) / MIN);
    magnitude = min > 0 ? `${hr} hr ${min} min` : `${hr} hr`;
  } else if (abs <= YEAR) {
    magnitude = `${(abs / DAY).toFixed(1)} days`;
  } else {
    // More than a year either way is not drift: the radio's clock was never set.
    return "clock not set";
  }
  return `${magnitude} ${direction}`;
}

// Implied time since power-up (2026-09-26 check-fixes batch 1, "CLOCK NOT
// SET SINCE POWER-UP TOO NARROW") — backend/amalgamesh/audit/clock.py's
// own `clock_not_set_uptime_s` (device_ts_median - RTC_RESET_SENTINEL),
// always non-negative when the flag is set. "about N ... ago" rather than
// a bare duration — this is describing how long ago the node's clock last
// reset, not a span between two evidence points.
export function formatUptimeSincePowerUp(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds) || seconds < 0) return "--";
  const MIN = 60;
  const HOUR = 3600;
  const DAY = 86400;
  if (seconds < HOUR) {
    const minutes = Math.max(1, Math.round(seconds / MIN));
    return `about ${minutes} min ago`;
  }
  if (seconds < DAY) {
    const hours = Math.round(seconds / HOUR);
    return `about ${hours} h ago`;
  }
  const days = Math.round(seconds / DAY);
  return `about ${days} day${days === 1 ? "" : "s"} ago`;
}

// Clock drift, seconds/day (signed — positive = device clock gaining
// time). "gains"/"loses" (the chosen wording, e.g. "gains 1.8 s a day").
export function formatClockDrift(sPerDay: number | null | undefined): string {
  if (sPerDay === null || sPerDay === undefined || Number.isNaN(sPerDay)) return "--";
  if (Math.abs(sPerDay) < 0.05) return "holds steady (no measurable drift)";
  const verb = sPerDay > 0 ? "gains" : "loses";
  const abs = Math.abs(sPerDay);
  const magnitude = abs < 100 ? abs.toFixed(1) : String(Math.round(abs));
  return `${verb} ${magnitude} s a day`;
}

// Sample span (2026-09-26 check-fixes third pass, fix 3 — "spanning X"):
// the real elapsed receipt-time a clock/advert sample covers, in DAYS from
// the API. Under an hour this used to render as the false-precision "0.2 h"
// — shown in MINUTES under an hour ("12 min"), HOURS under a day ("9 h"),
// and DAYS otherwise ("2.5 days"), so no tier ever collapses to a number
// under 1 of its own unit.
export function formatSampleSpan(days: number | null | undefined): string {
  if (days === null || days === undefined || Number.isNaN(days)) return "--";
  const hours = days * 24;
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} min`;
  }
  if (days < 1) {
    return `${Math.round(hours)} h`;
  }
  return `${days.toFixed(1)} day${days === 1 ? "" : "s"}`;
}

export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "--";
  return new Intl.NumberFormat().format(n);
}

export function formatRate(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "--";
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}

// Compact hex-id display — 10 hex characters plus an ellipsis. Extracted
// 2026-09-26 (Behaviour tab check-fixes batch 1, fix 6: "show the node's
// short hex ID ... same format used elsewhere in the app") from the exact
// truncation MeshCoreAirtimeAudit.tsx already used in three separate
// inline copies (`${id.slice(0, 10)}…`) as its own fallback label for an
// unnamed node/observer — this is that same established convention, given
// one shared name so a future caller (this one included) doesn't have to
// re-derive the length.
export function formatShortHexId(id: string): string {
  return `${id.slice(0, 10)}…`;
}

// Generic display-text truncation (Ask panel citations, 2026-09-20: the
// backend was putting a whole detector finding's multi-sentence summary
// into a citation's `label`, and the panel rendered it as a "pill" with no
// cap — 18 of them filled the entire panel, burying the actual answer
// above them). Applied defensively regardless of what any given backend
// field is documented to contain, since a future citation kind (or any
// other free-text field) isn't guaranteed to stay short just because this
// one now is.
export function truncateText(s: string, maxLength: number): string {
  const trimmed = s.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}
